import { createContext, use, useCallback, useEffect, useState, type ReactNode } from "react";
import type { ProfileResponse } from "@qota/shared";
import { getProfile } from "./profile";

/**
 * Stato di sessione condiviso da tutta l'applicazione.
 *
 * Prima ogni schermata chiamava `useProfile()` per conto suo, con due
 * conseguenze: `/me` veniva interrogata più volte per la stessa pagina, e —
 * peggio — dopo un accesso riuscito nessuno rileggeva lo stato se la
 * navigazione non cambiava rotta. Chi entrava con la passkey dalla schermata di
 * benvenuto restava fermo lì, autenticato ma senza saperlo.
 *
 * Con un contesto unico esiste una sola verità, e `refresh()` è il modo
 * esplicito di rileggerla dopo ogni operazione che cambia la sessione.
 */
export interface SessionState {
  profile: ProfileResponse | null;
  loading: boolean;
  /** `true` quando `/me` risponde 401: nessuna sessione valida. */
  unauthenticated: boolean;
  /**
   * Rilegge `/me`. Va chiamata dopo ogni accesso, uscita o modifica del
   * profilo: è ciò che tiene allineata l'interfaccia al cookie.
   */
  refresh: () => Promise<void>;
}

const SessionContext = createContext<SessionState | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [profile, setProfile] = useState<ProfileResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [unauthenticated, setUnauthenticated] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);

    try {
      const result = await getProfile();
      setProfile(result);
      setUnauthenticated(false);
    } catch {
      setProfile(null);
      setUnauthenticated(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return (
    <SessionContext value={{ profile, loading, unauthenticated, refresh }}>
      {children}
    </SessionContext>
  );
}

export function useSessionState(): SessionState {
  const context = use(SessionContext);

  if (!context) {
    throw new Error("useSessionState richiede SessionProvider");
  }

  return context;
}
